import _ from 'lodash';

import { COMMON_DATE_FORMAT, COMMON_DATETIME_FORMAT } from 'constants/date';
import { AttendantCompanyTypeEnum } from 'constants/loanApplication';
import { getValueOrDash } from 'utils/data';
import { formatDate as formatDateUtil } from 'utils/date';
import { formatPhoneNumberForDisplay } from 'utils/phone';

export const formatDate = (value, format = COMMON_DATE_FORMAT, options) => {
  if (!value) {
    return '-';
  }

  return formatDateUtil(value, format, options);
};

export const formatDateTime = value =>
  formatDate(value, COMMON_DATETIME_FORMAT, { includeLocalOffset: true });

export const formatCurrency = value => {
  if (_.isNil(value)) {
    return '-';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatPercentage = value => {
  if (_.isNil(value)) {
    return '-';
  }

  return `${Number(value).toFixed(2)}%`;
};

const formatPhoneForDisplay = value => (value ? formatPhoneNumberForDisplay(value) : '-');

export const getClosingCompanyInfo = closingCompanies => {
  if (!closingCompanies?.length) {
    return {
      companyName: '-',
      companyAddress: '-',
      closerName: '-',
      closerPhone: '-',
      closerEmail: '-',
      licenseNumber: '-',
      escrowNumber: '-',
      closerLicenseNumber: '-',
    };
  }

  const company = closingCompanies.find(
    c => c.closingCompanyType === AttendantCompanyTypeEnum.TITLE,
  );

  const address =
    [
      company?.streetAddress,
      company?.suite ? `Apt ${company.suite}` : null,
      company?.city,
      company?.state,
      company?.zipCode,
    ]
      .filter(Boolean)
      .join(', ') || '-';

  return {
    companyName: getValueOrDash(company?.name),
    companyAddress: address,
    closerName: getValueOrDash(company?.closerName),
    closerPhone: formatPhoneForDisplay(company?.closerContactPhone),
    closerEmail: getValueOrDash(company?.closerContactEmail),
    licenseNumber: getValueOrDash(company?.licenseNumber),
    escrowNumber: getValueOrDash(company?.escrowNumber),
    closerLicenseNumber: getValueOrDash(company?.closerLicenseNumber),
  };
};

export const getWireToInfo = ({ fundingInfo, warehouseBankOptions }) => {
  const wireToWarehouseBankName = getValueOrDash(
    warehouseBankOptions?.find(({ value }) => value === fundingInfo?.wireToInfoWarehouseBankId)
      ?.name,
  );

  const addressObj = fundingInfo?.closingAgentFeeProviderAddress;
  const companyAddress = addressObj
    ? [addressObj.street, addressObj.city, addressObj.state?.abbreviation, addressObj.zipCode]
        .filter(Boolean)
        .join(', ') || '-'
    : '-';

  return {
    feeProviderId: getValueOrDash(fundingInfo?.feeProviderId),
    companyName: getValueOrDash(fundingInfo?.companyName),
    companyAddress,
    bank: getValueOrDash(fundingInfo?.bank),
    cityAndState: [fundingInfo?.city, fundingInfo?.state].filter(Boolean).join(', ') || '-',
    closingAgentABANumber: getValueOrDash(fundingInfo?.closingAgentABANumber),
    closingAgentAccountNumber: getValueOrDash(fundingInfo?.closingAgentAccountNumber),
    referenceNumber: getValueOrDash(fundingInfo?.referenceNumber),
    closingAgentContact: getValueOrDash(fundingInfo?.closingAgentContact),
    warehouseBank: wireToWarehouseBankName,
    wireNumber: getValueOrDash(fundingInfo?.wireNumber),
    fedReferenceNumber: getValueOrDash(fundingInfo?.fedReferenceNumber),
    wireAmount: formatCurrency(fundingInfo?.wireToInfoWireAmount),
    advanceAmountPercent: formatPercentage(fundingInfo?.advanceAmountPercent),
    advanceAmount: formatCurrency(fundingInfo?.advanceAmount),
    addAdvanceAmount: formatCurrency(fundingInfo?.addAdvanceAmount),
  };
};
